import {
  Divider,
  Flex,
  Form,
  Header,
  PrimaryButton,
  SecondaryButton,
  Space,
  SubText,
  TertiaryButton,
  Text,
  WithFormControlSelect,
} from '@procurify/ui'
import { Accordion } from '@procurify/ui'
import { ErrorAlert, WarningAlert } from '@procurify/ui'
import { type IFormProps } from '@procurify/ui'
import {
  FieldArray,
  type FormikErrors,
  FormikProvider,
  useFormik,
} from 'formik'
import { DateTime } from 'luxon'
import moment, { type Moment } from 'moment'
import { useSelector } from 'react-redux'
import { type ActionMeta } from 'react-select'
import * as Yup from 'yup'

import { AccountCodeSelect } from '@webapp/components/AccountCodeSelect'
import { DepartmentSelect } from '@webapp/components/DepartmentSelect'
import { PriceFormField } from '@webapp/components/PriceField'
import { UserSelect } from '@webapp/components/UserSelect'
import { makeSelectFeatureSwitches } from '@webapp/core/stores/global'
import { useProcurifyIntl } from '@webapp/hooks'
import { type AccountCode, type Department, type User } from '@webapp/models'
import {
  type BudgetIntervalType,
  BudgetIntervalTypes,
} from '@webapp/models/enums/BudgetInterval'
import { Permissions } from '@webapp/platform/iam'
import { calculateNumberOfPeriods } from '@webapp/utils/spend/generateBudgets'
import messages from './messages'
import { BudgetIntervalSelect } from '../BudgetIntervalSelect'
import { BudgetCategoryFormStyled } from './styles/BudgetCategoryFormStyled'
import {
  type BudgetCategoryFormFormSchema,
  type BudgetGroupErrors,
  type BudgetGroupFormSchema,
  type DateRangeErrorDetails,
  DateRangeErrorTypes,
  type IBudgetCategoryFormProps,
} from './types'
import { type BudgetIntervalOption } from '../BudgetIntervalSelect/BudgetIntervalSelect'

const DATE_RANGE_PICKER_PRESETS = [
  {
    label: 'This Month',
    start: moment().startOf('month'),
    end: moment().endOf('month'),
  },
  {
    label: 'This Year',
    start: moment().startOf('year'),
    end: moment().endOf('year'),
  },
]

export const BudgetCategoryForm = ({
  initialValues,
  isRequesterBudgetVisibilityEnabled = false,
  onCancel = () => undefined,
  onSubmit = () => undefined,
}: IBudgetCategoryFormProps) => {
  const intl = useProcurifyIntl()
  const { enable_flexible_spend_tracking: isFlexibleSpendTrackingEnabled } =
    useSelector(makeSelectFeatureSwitches())

  const USER_SELECT_PERMISSIONS_FILTER = [
    Permissions.APPROVE_EXPENSEREPORT,
    Permissions.APPROVE_ORDER,
    Permissions.APPROVE_PAY_REQUEST,
    ...(isRequesterBudgetVisibilityEnabled
      ? [
          Permissions.ADD_ORDER,
          Permissions.ADD_EXPENSE,
          Permissions.ADD_REQUEST,
        ]
      : []),
  ]

  const validateBudgetGroupFields = {
    is: true,
    then: Yup.array(
      Yup.object<BudgetGroupFormSchema>({
        dates: Yup.object<BudgetGroupFormSchema['dates']>({
          startDate: Yup.object<Moment>()
            .nullable()
            .required(intl.formatMessage(messages.startDateRequiredError)),
          endDate: Yup.object<Moment>()
            .nullable()
            .required(intl.formatMessage(messages.endDateRequiredError)),
        }),
        budgets: Yup.array().of(
          Yup.number()
            .nullable()
            .moreThan(
              0,
              intl.formatMessage(messages.budgetFieldGreaterThanZeroError)
            )
            .required(intl.formatMessage(messages.budgetFieldRequiredError))
        ),
      })
    ),
    otherwise: Yup.array(
      Yup.object<BudgetGroupFormSchema>({
        dates: Yup.object<BudgetGroupFormSchema['dates']>({
          startDate: Yup.object<Moment>()
            .nullable()
            .required(intl.formatMessage(messages.startDateRequiredError)),
          endDate: Yup.object<Moment>()
            .nullable()
            .required(intl.formatMessage(messages.endDateRequiredError)),
        }).nullable(),
        budgets: Yup.array(),
      })
    ),
  }

  const validationSchema = Yup.object<BudgetCategoryFormFormSchema>().shape(
    {
      name: Yup.string().required(
        intl.formatMessage(messages.nameFieldRequiredError)
      ),
      departments: Yup.array(Yup.object<Department>()).when('accountCodes', {
        is: (accountCodes) => !accountCodes || accountCodes?.length === 0,
        then: Yup.array(Yup.object<Department>())
          .nullable()
          .required(
            intl.formatMessage(
              messages.departmentOrAccountCodeFieldsRequiredError
            )
          ),
        otherwise: Yup.array(Yup.object<Department>()).nullable(),
      }),
      accountCodes: Yup.array(Yup.object<AccountCode>()).when('departments', {
        is: (departments) => !departments || departments?.length === 0,
        then: Yup.array(Yup.object<AccountCode>())
          .nullable()
          .required(
            intl.formatMessage(
              messages.departmentOrAccountCodeFieldsRequiredError
            )
          ),
        otherwise: Yup.array(Yup.object<AccountCode>()).nullable(),
      }),
      users: Yup.array<User>()
        .nullable()
        .required(intl.formatMessage(messages.usersViewAccessRequiredError)),
      isSourceOfTruth: Yup.boolean().nullable(),
      hasBudgetFields: Yup.boolean().nullable(),
      hasCustomBudgets: Yup.boolean().nullable(),
      budgetInterval: Yup.object<BudgetIntervalOption>()
        .when('hasBudgetFields', {
          is: true,
          then: Yup.object<BudgetIntervalOption>().required(
            intl.formatMessage(messages.budgetPeriodRequiredError)
          ),
        })
        .nullable(),
      budgetGroups: Yup.array(
        Yup.object<BudgetGroupFormSchema>()
          .test(
            'isDateRangeTooLarge',
            DateRangeErrorTypes.TOO_LARGE,
            function test(
              group: BudgetGroupFormSchema
            ): true | Yup.ValidationError {
              const { startDate, endDate } = group.dates
              if (startDate && endDate) {
                const diff = endDate.diff(startDate, 'months', true)
                if (diff > 60) {
                  return this.createError({
                    path: `${this.path}.dateRange`,
                    message: DateRangeErrorTypes.TOO_LARGE,
                  })
                }
              }
              return true
            }
          )
          .test(
            'noOverlap',
            DateRangeErrorTypes.OVERLAP,
            function test(
              group: BudgetGroupFormSchema
            ): true | Yup.ValidationError {
              const { startDate, endDate } = group.dates
              if (!startDate || !endDate) {
                return true
              }

              const itemIndex = this.parent.indexOf(group)
              if (itemIndex === 0) {
                return true
              }

              const newStartDate = startDate.valueOf()
              const newEndDate = endDate.valueOf()
              /**
               * Since we only need to validate date ranges in sequential order
               * slice off any ranges that come after the selected one.
               */
              const noOverlap: boolean = this.parent
                .slice(0, itemIndex)
                .every((range) => {
                  const { startDate: rStartDate, endDate: rEndDate } =
                    range.dates
                  if (!rStartDate || !rEndDate) {
                    return true
                  }

                  const existingStartDate = rStartDate.valueOf()
                  const existingEndDate = rEndDate.valueOf()

                  if (
                    existingEndDate < newStartDate ||
                    existingStartDate > newEndDate
                  ) {
                    return true
                  }
                  return false
                })

              if (!noOverlap) {
                return this.createError({
                  path: `${this.path}.dateRange`,
                  message: DateRangeErrorTypes.OVERLAP,
                })
              } else {
                return true
              }
            }
          )
      ).when('hasBudgetFields', validateBudgetGroupFields),
    },
    [['accountCodes', 'departments']]
  )

  const initialFormValues = {
    name: '',
    departments: [],
    accountCodes: [],
    users: [],
    isSourceOfTruth: false,
    hasBudgetFields: false,
    hasCustomBudgets: false,
    budgetInterval: null,
    budgetGroups: [],
    ...initialValues,
  }

  const handleSubmit: IFormProps<BudgetCategoryFormFormSchema>['onSubmit'] = (
    formValues,
    formikActions
  ) => {
    onSubmit(formValues, formikActions)
  }

  const formikContext = useFormik<BudgetCategoryFormFormSchema>({
    initialValues: initialFormValues,
    validationSchema,
    onSubmit: handleSubmit,
  })

  const { isSubmitting, values, errors, setValues, isValid } = formikContext
  const { budgetGroups } = values

  const enableAddDateRange =
    budgetGroups.length === 0 ||
    (budgetGroups.length > 0 &&
      !!budgetGroups[budgetGroups.length - 1].dates.startDate &&
      !!budgetGroups[budgetGroups.length - 1].dates.endDate)

  const resetBudgetGroupBudgetValues = (
    budgetGroupFields: BudgetGroupFormSchema[],
    options: {
      newBudgetPeriod?: BudgetIntervalType
      newHasCustomBudgets?: boolean
    }
  ) => {
    const { newBudgetPeriod, newHasCustomBudgets } = options
    const budgetPeriod = newBudgetPeriod || values.budgetInterval.value
    const hasCustomBudgets =
      newHasCustomBudgets === undefined
        ? values.hasCustomBudgets
        : newHasCustomBudgets
    const updatedBudgetGroupFields = [...budgetGroupFields]

    updatedBudgetGroupFields.forEach((budgetGroup) => {
      const { dates } = budgetGroup
      const { startDate, endDate } = dates
      const dateRangeStart = startDate
        ? DateTime.fromISO(startDate.format('YYYY-MM-DD')).startOf('day')
        : undefined
      const dateRangeEnd = endDate
        ? DateTime.fromISO(endDate.format('YYYY-MM-DD')).endOf('day')
        : undefined
      const hasDates = dateRangeStart && dateRangeEnd

      if (hasCustomBudgets && hasDates) {
        budgetGroup.budgets = Array(
          calculateNumberOfPeriods(dateRangeStart, dateRangeEnd, budgetPeriod)
        ).fill(null)
      } else if (values.hasBudgetFields) {
        budgetGroup.budgets = [null]
      } else {
        budgetGroup.budgets = []
      }

      return budgetGroup
    })

    return updatedBudgetGroupFields
  }

  const handleHasBudgetFieldsClick = () => {
    const shouldResetBudgetFormFields =
      values.hasBudgetFields && values.budgetInterval && values.budgetGroups

    if (shouldResetBudgetFormFields) {
      setValues({
        ...values,
        budgetInterval: null,
        hasCustomBudgets: false,
        budgetGroups: resetBudgetGroupBudgetValues(values.budgetGroups, {
          newBudgetPeriod: null,
          newHasCustomBudgets: false,
        }),
      })
    }
  }

  const handleHasCustomBudgetsClick = () => {
    setValues({
      ...values,
      hasCustomBudgets: !values.hasCustomBudgets,
      budgetGroups: resetBudgetGroupBudgetValues(values.budgetGroups, {
        newHasCustomBudgets: !values.hasCustomBudgets,
      }),
    })
  }

  const handleBudgetIntervalChange = (
    newValue: BudgetIntervalOption,
    actionMeta: ActionMeta<BudgetIntervalOption>
  ) => {
    if (actionMeta.action === 'clear') {
      setValues({
        ...values,
        budgetInterval: null,
        hasCustomBudgets: false,
        budgetGroups: resetBudgetGroupBudgetValues(values.budgetGroups, {
          newHasCustomBudgets: false,
          newBudgetPeriod: null,
        }),
      })
    }

    if (actionMeta.action === 'select-option') {
      if (newValue.value === BudgetIntervalTypes.ENTIRE_DATE_RANGE) {
        setValues({
          ...values,
          budgetInterval: newValue,
          hasCustomBudgets: false,
          budgetGroups: resetBudgetGroupBudgetValues(values.budgetGroups, {
            newHasCustomBudgets: false,
            newBudgetPeriod: newValue.value,
          }),
        })
      } else {
        setValues({
          ...values,
          budgetInterval: newValue,
          budgetGroups: resetBudgetGroupBudgetValues(values.budgetGroups, {
            newBudgetPeriod: newValue.value,
          }),
        })
      }
    }
  }

  const addDateRange = (
    push: (newBudgetGroup: BudgetGroupFormSchema) => void
  ) => {
    const { hasBudgetFields, hasCustomBudgets } = values
    let budgets = []
    if (hasBudgetFields && !hasCustomBudgets) {
      budgets = [null]
    }

    push({
      dates: {
        startDate: null,
        endDate: null,
      },
      budgets,
    })
  }

  /**
   * For custom budgets changes the form validation to properly match the number of inputs being rendered by
   * updating the date ranges budgets array length.
   */
  const adjustNumberOfCustomPeriodsInDateRange = (
    startDate: Moment,
    endDate: Moment,
    selectedInterval: BudgetIntervalType,
    index: number
  ) => {
    const dateRangeStart = startDate
      ? DateTime.fromISO(startDate.format('YYYY-MM-DD')).startOf('day')
      : undefined
    const dateRangeEnd = endDate
      ? DateTime.fromISO(endDate.format('YYYY-MM-DD')).endOf('day')
      : undefined

    if (!dateRangeStart || !dateRangeEnd) {
      return
    }

    // 1 indexed
    const expectedNumberOfPeriods: number = calculateNumberOfPeriods(
      dateRangeStart,
      dateRangeEnd,
      selectedInterval
    )
    // 1 indexed
    const actualNumberOfPeriods: number =
      values.budgetGroups[index].budgets.length
    const newBudgetGroups = [...values.budgetGroups]

    if (expectedNumberOfPeriods > actualNumberOfPeriods) {
      const newBudgets = Array(
        expectedNumberOfPeriods - actualNumberOfPeriods
      ).fill(null)
      newBudgetGroups[index].budgets.push(...newBudgets)
    }

    if (expectedNumberOfPeriods < actualNumberOfPeriods) {
      const firstIndexToRemove = expectedNumberOfPeriods
      newBudgetGroups[index].budgets.splice(firstIndexToRemove)
    }

    newBudgetGroups[index].dates = {
      startDate,
      endDate,
    }

    setValues({
      ...values,
      budgetGroups: newBudgetGroups,
    })
  }

  const renderCustomBudgetFields = (
    key: string,
    selectedInterval: BudgetIntervalType,
    dateRangeStart: DateTime,
    dateRangeEnd: DateTime
  ): JSX.Element[] => {
    const fields: JSX.Element[] = []
    const numberOfPeriods = calculateNumberOfPeriods(
      dateRangeStart,
      dateRangeEnd,
      selectedInterval
    )

    let currentDate: DateTime
    let incrementObject: { months?: number; quarters?: number; years?: number }

    switch (selectedInterval) {
      case BudgetIntervalTypes.QUARTERLY:
        currentDate = dateRangeStart.startOf('quarter')
        incrementObject = { quarters: 1 }
        break

      case BudgetIntervalTypes.MONTHLY:
        currentDate = dateRangeStart.startOf('month')
        incrementObject = { months: 1 }
        break

      case BudgetIntervalTypes.YEARLY:
      default:
        currentDate = dateRangeStart.startOf('year')
        incrementObject = { years: 1 }
        break
    }

    for (let budgetNum = 0; budgetNum < numberOfPeriods; budgetNum++) {
      let label: string
      switch (selectedInterval) {
        case BudgetIntervalTypes.QUARTERLY:
          label = currentDate.toFormat("'Q'q yyyy")
          break

        case BudgetIntervalTypes.MONTHLY:
          label = currentDate.toFormat('MMM yyyy')
          break

        case BudgetIntervalTypes.YEARLY:
        default:
          label = currentDate.toFormat('yyyy')
          break
      }

      fields.push(
        <Flex.Item key={`${key}.budgets[${budgetNum}]`} width={1 / 4}>
          <PriceFormField
            fullWidth={false}
            key={`${key}.budgets[${budgetNum}]`}
            placeholder={intl.formatMessage(messages.budgetFieldPlaceholder)}
            name={`${key}.budgets[${budgetNum}]`}
            id={`${key}.budgets[${budgetNum}]`}
            label={label}
            required
          />
        </Flex.Item>
      )

      currentDate = currentDate.plus(incrementObject)
    }
    return fields
  }

  const renderDateRange = (
    budgetGroup: BudgetGroupFormSchema,
    index: number,
    remove: (index: number) => BudgetGroupFormSchema
  ) => {
    if (!budgetGroup) return

    const { budgetGroups: budgetGroupErrors } = errors

    const dates = budgetGroup.dates || {
      startDate: undefined,
      endDate: undefined,
    }
    const { startDate, endDate } = dates
    let hasDateRangeErrors = false

    const dateRangeStart = startDate
      ? DateTime.fromISO(startDate.format('YYYY-MM-DD')).startOf('day')
      : undefined
    const dateRangeEnd = endDate
      ? DateTime.fromISO(endDate.format('YYYY-MM-DD')).endOf('day')
      : undefined
    const hasDates = dateRangeStart && dateRangeEnd

    const selectedInterval: BudgetIntervalType = values.budgetInterval
      ? values.budgetInterval.value
      : undefined

    const getErrorMessage = (dateRangeError: string): DateRangeErrorDetails => {
      hasDateRangeErrors = true

      if (dateRangeError === DateRangeErrorTypes.TOO_LARGE) {
        return {
          title: intl.formatMessage(messages.dateRangeTooLargeErrorTitle),
          msg: intl.formatMessage(messages.dateRangeTooLargeErrorMsg),
        }
      } else if (dateRangeError === DateRangeErrorTypes.OVERLAP) {
        return {
          title: intl.formatMessage(messages.dateRangeOverlapErrorTitle),
          msg: intl.formatMessage(
            isFlexibleSpendTrackingEnabled
              ? messages.dateRangeOverlapErrorMsg
              : messages.oldDateRangeOverlapErrorMsg
          ),
        }
      }
    }

    const isPartialDateRange = (): boolean => {
      if (
        !selectedInterval ||
        selectedInterval === BudgetIntervalTypes.ENTIRE_DATE_RANGE
      ) {
        return false
      }

      if (startDate && endDate && values.hasBudgetFields && selectedInterval) {
        switch (selectedInterval) {
          case BudgetIntervalTypes.MONTHLY:
            return (
              dateRangeStart.toMillis() !==
                dateRangeStart.startOf('month').toMillis() ||
              dateRangeEnd.toMillis() !== dateRangeEnd.endOf('month').toMillis()
            )

          case BudgetIntervalTypes.QUARTERLY:
            return (
              dateRangeStart.toMillis() !==
                dateRangeStart.startOf('quarter').toMillis() ||
              dateRangeEnd.toMillis() !==
                dateRangeEnd.endOf('quarter').toMillis()
            )

          case BudgetIntervalTypes.YEARLY:
            return (
              dateRangeStart.toMillis() !==
                dateRangeStart.startOf('year').toMillis() ||
              dateRangeEnd.toMillis() !== dateRangeEnd.endOf('year').toMillis()
            )

          default:
            break
        }
      }
      return false
    }

    const isBudgetGroupError = (
      error: string | string[] | FormikErrors<BudgetGroupErrors>[]
    ): error is FormikErrors<BudgetGroupErrors>[] => {
      return (
        budgetGroupErrors &&
        budgetGroupErrors[index] &&
        (error as FormikErrors<BudgetGroupErrors>[])[index]?.dateRange !==
          undefined
      )
    }

    const shouldRenderWarning = isPartialDateRange()

    const errorDetails = isBudgetGroupError(budgetGroupErrors)
      ? getErrorMessage(budgetGroupErrors[index].dateRange)
      : null

    const accordionTitle =
      startDate && endDate
        ? `${startDate.format('MMM DD YYYY').toString()} - ${endDate
            .format('MMM DD YYYY')
            .toString()}`
        : intl.formatMessage(messages.newDateRangeTitle)

    const shouldExpandAccordion =
      accordionTitle === intl.formatMessage(messages.newDateRangeTitle) ||
      hasDateRangeErrors

    const key = `budgetGroups.${index}`

    return (
      <Accordion
        key={key}
        title={<Text bold>{accordionTitle}</Text>}
        isExpandedControlled={shouldExpandAccordion}
        collapsible={!hasDateRangeErrors}
      >
        <Space>
          <Flex.Row>
            <Flex.Item>
              <Form.DateRangePicker
                id={`${key}.dates`}
                label={intl.formatMessage(messages.dateRangeLabel)}
                name={`${key}.dates`}
                required
                presets={DATE_RANGE_PICKER_PRESETS}
                hideKeyboardShortcutsPanel={true}
                fullWidth
                keepOpenOnDateSelect
                onClose={(newDates: { startDate: Moment; endDate: Moment }) => {
                  if (values.hasCustomBudgets) {
                    adjustNumberOfCustomPeriodsInDateRange(
                      newDates.startDate,
                      newDates.endDate,
                      selectedInterval,
                      index
                    )
                  }
                }}
              />
            </Flex.Item>
            {values.budgetInterval &&
              !values.hasCustomBudgets &&
              !errorDetails && (
                <Flex.Item>
                  <PriceFormField
                    fullWidth={false}
                    placeholder={intl.formatMessage(
                      messages.budgetFieldPlaceholder
                    )}
                    name={`${key}.budgets[0]`}
                    id={`${key}.budgets[0]`}
                    label={intl.formatMessage(messages.budgetFieldLabel)}
                    required
                  />
                </Flex.Item>
              )}
          </Flex.Row>
          {values.hasCustomBudgets && hasDates && !errorDetails && (
            <Flex.Row>
              {renderCustomBudgetFields(
                key,
                selectedInterval,
                dateRangeStart,
                dateRangeEnd
              )}
            </Flex.Row>
          )}
          {errorDetails && (
            <ErrorAlert>
              <SubText>
                <strong>{errorDetails.title}</strong>
                <br />
                <span>{errorDetails.msg}</span>
              </SubText>
            </ErrorAlert>
          )}
          {shouldRenderWarning && (
            <WarningAlert>
              <SubText>
                <strong>
                  {intl.formatMessage(
                    messages.dateRangePartialCoverageWarningTitle
                  )}
                </strong>
                <br />
                <span>
                  {intl.formatMessage(
                    messages.dateRangePartialCoverageWarningMsg
                  )}
                </span>
              </SubText>
            </WarningAlert>
          )}
          <SecondaryButton
            type='button'
            onClick={() => remove(index)}
            icon='delete'
          >
            {intl.formatMessage(messages.deleteDateRangeButton)}
          </SecondaryButton>
        </Space>
      </Accordion>
    )
  }

  return (
    <BudgetCategoryFormStyled>
      <FormikProvider value={formikContext}>
        <Form.Content noValidate autoComplete='off'>
          <Space align='stretch' size='xlarge'>
            <Space align='stretch'>
              <Form.TextField
                id='name'
                name='name'
                placeholder={intl.formatMessage(
                  isFlexibleSpendTrackingEnabled
                    ? messages.nameFieldPlaceholder
                    : messages.oldNameFieldPlaceholder
                )}
                label={intl.formatMessage(messages.nameFieldLabel)}
                required
              />
              <Space size='small' align='stretch'>
                <Text>
                  {intl.formatMessage(
                    messages.budgetCategoryAttributesInstructionText
                  )}
                </Text>
                <Flex.Row>
                  <Flex.Item width={[1 / 2]}>
                    <WithFormControlSelect
                      id='departments'
                      name='departments'
                      label={intl.formatMessage(messages.departmentsFieldLabel)}
                    >
                      <DepartmentSelect
                        isMulti
                        closeMenuOnSelect={false}
                        getOptionLabel={(option) =>
                          `${option?.branch?.name} - ${option.name}`
                        }
                      />
                    </WithFormControlSelect>
                  </Flex.Item>
                  <Flex.Item width={[1 / 2]}>
                    <WithFormControlSelect
                      id='accountCodes'
                      name='accountCodes'
                      label={intl.formatMessage(
                        messages.accountCodesFieldLabel
                      )}
                    >
                      <AccountCodeSelect isMulti closeMenuOnSelect={false} />
                    </WithFormControlSelect>
                  </Flex.Item>
                </Flex.Row>
              </Space>
              <WithFormControlSelect
                id='users'
                name='users'
                label={intl.formatMessage(messages.usersViewAccessFieldLabel)}
                required
              >
                <UserSelect
                  isMulti
                  closeMenuOnSelect={false}
                  permissions={USER_SELECT_PERMISSIONS_FILTER}
                />
              </WithFormControlSelect>
              <Form.Checkbox
                id='isSourceOfTruth'
                name='isSourceOfTruth'
                inputProps={{
                  'aria-label': intl.formatMessage(
                    isFlexibleSpendTrackingEnabled
                      ? messages.isSourceOfTruthLabel
                      : messages.oldIsSourceOfTruthLabel
                  ),
                }}
              >
                {intl.formatMessage(
                  isFlexibleSpendTrackingEnabled
                    ? messages.isSourceOfTruthLabel
                    : messages.oldIsSourceOfTruthLabel
                )}
              </Form.Checkbox>
            </Space>

            <Space>
              <Header as='h5' gutter={false}>
                {intl.formatMessage(messages.budgetsHeader)}
              </Header>

              <Form.Checkbox
                id='hasBudgetFields'
                name='hasBudgetFields'
                inputProps={{
                  'aria-label': intl.formatMessage(
                    messages.setBudgetFieldsForDateRangesLabel
                  ),
                }}
                onClick={handleHasBudgetFieldsClick}
              >
                {intl.formatMessage(messages.setBudgetFieldsForDateRangesLabel)}
              </Form.Checkbox>
              {values.hasBudgetFields && (
                <Flex.Row spacing={1.5}>
                  <Flex.Item width={[1 / 4]}>
                    <WithFormControlSelect
                      id='budgetInterval'
                      name='budgetInterval'
                      label={intl.formatMessage(
                        messages.budgetPeriodFieldLabel
                      )}
                      required={values.hasBudgetFields}
                    >
                      <BudgetIntervalSelect
                        onChange={handleBudgetIntervalChange}
                      />
                    </WithFormControlSelect>
                  </Flex.Item>
                  <Flex.Item width={1 / 4} mt='24px'>
                    <Form.Checkbox
                      id='hasCustomBudgets'
                      name='hasCustomBudgets'
                      disabled={
                        !values.budgetInterval ||
                        values?.budgetInterval?.value ===
                          BudgetIntervalTypes.ENTIRE_DATE_RANGE
                      }
                      inputProps={{
                        'aria-label': intl.formatMessage(
                          messages.customBudgetCheckboxLabel
                        ),
                      }}
                      onClick={handleHasCustomBudgetsClick}
                    >
                      {intl.formatMessage(messages.customBudgetCheckboxLabel)}
                    </Form.Checkbox>
                  </Flex.Item>
                </Flex.Row>
              )}

              <FieldArray name='budgetGroups'>
                {({ remove, push }) => (
                  <Space>
                    {budgetGroups.map((budgetGroup, index) =>
                      renderDateRange(budgetGroup, index, remove)
                    )}
                    <Accordion
                      title={
                        <Text bold>
                          {intl.formatMessage(messages.addDateRangeTitle)}
                        </Text>
                      }
                      expandIcon='add'
                      expandable={enableAddDateRange}
                      onChange={() => {
                        if (enableAddDateRange) {
                          addDateRange(push)
                        }
                      }}
                    />
                  </Space>
                )}
              </FieldArray>
            </Space>

            <Divider space={0} />

            <Space size='large' direction='horizontal' justify='end'>
              <TertiaryButton type='button' onClick={onCancel}>
                {intl.formatMessage(messages.cancelButton)}
              </TertiaryButton>
              <PrimaryButton type='submit' disabled={isSubmitting || !isValid}>
                {initialValues
                  ? intl.formatMessage(messages.submitEditButton)
                  : intl.formatMessage(messages.submitCreateButton)}
              </PrimaryButton>
            </Space>
          </Space>
        </Form.Content>
      </FormikProvider>
    </BudgetCategoryFormStyled>
  )
}
